// Card 52, battle marks (R548, ui-map-interface.md §17.3.52).
//
// An old map set crossed swords where a battle was fought and the year
// beside them. The mark here stands where a hull went down inside the last
// world day, this commander's own or one their fire sank, and it is the
// DOOR to the record: a press opens the replay film of R531 at that window,
// so the map remembers where it bled and a reader reaches the film from the
// place rather than from the INBOX list.
//
// A fight that sank nothing leaves no mark (R617): after a seaquake every
// border skirmish wrote one, and the busy map read as clutter. The marks
// are the commander's own messages, because the other side's losses are
// not a fact the server gives away (§5). They fade over the day and then
// leave. A mark the commander read fades more and drops its label, and
// `max_marks` bounds how many marks the piece draws at all.
//
// **THE PRESS.** `commands.clickMap` asks the furniture LAST, after the
// armed pick mode, the fleets and the sites (R702), and `pick` here answers
// with the mark's own id. `main.ts` holds the other half: it finds the
// message and opens its replay. A mark over a hull opens nothing, because
// the hull takes the click: the record must never cost the player a
// selection or an order. A finger opens a mark too, under the same rule:
// the tap grammar's `deselect` branch is the only one that reaches here.

import { chart as S } from '../../../gen/strings';
import { swords } from '../../../render/chart-glyphs';
import { hullKey } from '../../gfx/atlas';
import { DEFAULT_SHIP_STYLE } from '../../../render/hull-styles';
import { cellCenter } from '../../../world';
import { InboxKind, ShipClass, type InboxMsg } from '../../../gen/proto/aquaphobia';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece, PickHit } from '../context';
import { hhmm } from '../../../locale/clock';
import { CHART_INK, faceOf, inkOf } from './shared';

/** The two message kinds that report a sunk hull (R617). */
const SINK_KINDS: readonly InboxKind[] = [
  InboxKind.INBOX_KIND_FLEET_LOST,
  InboxKind.INBOX_KIND_FLEET_KILLED,
];

/** One sinking of the last day. */
interface Mark {
  /** The piece's own id for the mark: the inbox message it opens. */
  id: string;
  x: number;
  y: number;
  /** The season day the fight ended on. */
  day: number;
  /** The world time of day it ended at, as `hh:mm`. */
  at: string;
  /** How far through the window the fight is, 0 (new) to 1 (a day old). */
  age: number;
  /** Can this mark open a film at all? */
  hasReplay: boolean;
  /** Did the commander read the message this mark opens? */
  read: boolean;
}

/** One live sinking: a message the piece draws a mark for, and its age. */
export interface LiveSinking {
  msg: InboxMsg;
  /** World seconds since the message, on the world clock. */
  ago: number;
}

/**
 * The sinkings that stand on the map as battle marks now, newest first.
 *
 * This is THE rule of the marks (R617, R694), and it is written once: the
 * two SINK kinds, a `cell` that is not 0, younger than `window_world_sec` on
 * the world clock, and at most `max_marks` of them. The marks piece draws
 * these, and the wrecks piece (card 08, R801) hides the wreck of every hull
 * they name, so the two can never disagree on which marks exist.
 */
export function liveSinkings(ctx: ChartContext, nowSec = Date.now() / 1000): LiveSinking[] {
  const t = ctx.cfg.pieces.battle_marks;
  const state = ctx.state;
  const out: LiveSinking[] = [];
  for (const msg of state.inbox) {
    // The window, then the ceiling. The window is the rule; the ceiling
    // is a guard, so a busy day can never fill the water with darts.
    // The server sends the inbox newest first, so the marks kept here
    // are the newest ones.
    if (out.length >= t.fig.max_marks) break;
    if (!SINK_KINDS.includes(msg.kind) || msg.cell === 0) continue;
    const ago = Math.max(0, (nowSec - msg.unix) * state.timescale);
    if (ago > t.fig.window_world_sec) continue;
    out.push({ msg, ago });
  }
  return out;
}

/** The mark id a press answers with, so `main.ts` reads one shape. */
export const MARK_PREFIX = 'battle:';

/** The inbox message id inside a mark id, or 0 when the id is not one. */
export function markMessageId(id: string): number {
  if (!id.startsWith(MARK_PREFIX)) return 0;
  const n = Number(id.slice(MARK_PREFIX.length));
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export class BattleMarksPiece implements Piece {
  readonly id = 'battle_marks';
  readonly tier = 'screen';
  readonly pack = 'static';
  readonly film = 'hide';
  readonly rebuildOn = ['hello', 'inbox', 'minute', 'reading', 'font'] as const;

  /** The sinkings of the last world day, newest first. The tests read them. */
  marks: Mark[] = [];

  /** The pick tolerance of the last pack, in world px. */
  private tolPx = 0;

  rebuild(ctx: ChartContext): void {
    const t = ctx.cfg.pieces.battle_marks;
    this.tolPx = t.fig.pick_px;
    this.marks = [];
    const state = ctx.state;
    const clockS = state.worldClockSec();
    // The day the season stands at now. A fight of yesterday reads one day
    // less, and the season's own length comes off the server's pack.
    const elapsed = state.seasonElapsedSec();
    for (const { msg, ago } of liveSinkings(ctx)) {
      const [x, y] = cellCenter(msg.cell);
      this.marks.push({
        id: `${MARK_PREFIX}${msg.id}`,
        x, y,
        day: Math.max(1, Math.floor((elapsed - ago) / 86400) + 1),
        at: hhmm(clockS - ago),
        age: Math.min(1, ago / Math.max(1, t.fig.window_world_sec)),
        hasReplay: msg.replay !== undefined,
        read: msg.read,
      });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const t = ctx.cfg.pieces.battle_marks;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    // The fleet layer's own dart cell, asked for once a pack. The dart is
    // the same shape in every ship icon set, so it takes the AUTO set (R835).
    const dart = ctx.glyphs.mark(
      hullKey(DEFAULT_SHIP_STYLE, ShipClass.SHIP_CLASS_UNSPECIFIED, false),
    );
    for (const m of this.marks) {
      // A mark the commander read keeps its crossed darts and loses its
      // label. The words are the clutter, and the darts are the memory.
      const lit = t.fig.alpha_new + (t.fig.alpha_old - t.fig.alpha_new) * m.age;
      const alpha = m.read ? lit * t.fig.alpha_read_mul : lit;
      if (hist) {
        swords(sinks.segments, sinks.circles, m.x, m.y, ink, alpha, r.width);
        if (!m.read) {
          ctx.glyphs.word(
            sinks.quads, S.mark_historical({ day: m.day }),
            m.x + t.fig.label_dx, m.y, face, t.text.main, ink, alpha * r.alpha,
          );
        }
        continue;
      }
      // Two darts crossed over the water, as the card set them: the pair
      // stands 45 degrees either side of straight up.
      for (let s = -1; s <= 1; s += 2) {
        sinks.quads.sprite(
          m.x, m.y, -Math.PI / 2 + (s * Math.PI) / 4, dart,
          ink, alpha, t.fig.dart_scale,
        );
      }
      if (!m.read) {
        ctx.glyphs.word(
          sinks.quads,
          S.mark_modern({ day: m.day, at: m.at }),
          m.x + t.fig.label_dx, m.y, face, t.text.main, ink, alpha * r.alpha,
        );
      }
    }
  }

  /**
   * The nearest sinking under a world point, or null.
   *
   * A mark that carries no film answers nothing: a press that opened
   * nothing would still have eaten the click that would have selected the
   * water under it.
   */
  pick(wx: number, wy: number, tolPx: number): PickHit | null {
    const tol = Math.max(tolPx, this.tolPx);
    let best: PickHit | null = null;
    let bestD = Infinity;
    for (const m of this.marks) {
      if (!m.hasReplay) continue;
      const d = Math.hypot(m.x - wx, m.y - wy);
      if (d <= tol && d < bestD) {
        best = { kind: 'mark', id: m.id, x: m.x, y: m.y };
        bestD = d;
      }
    }
    return best;
  }
}

/** The registry's entry. */
export const battleMarks = new BattleMarksPiece();
